import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Hourglass, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { apiAssetSrc } from '../../lib/apiAsset';
import './FindYourClub.css';

/**
 * B-58 slices 2–3 (Stuart, 12 Oct: all ★): "Find your club".
 *
 * Type a club's name or town → GET /club-search (signed in; verified, active
 * clubs only; at most 10 rows of name / sport / town / logo) → tap a result →
 * that club's hub, where the existing Request to join takes over (no Coach;
 * every B-58 rule stays server-side). Clubs they're already in say "Your
 * club"; ones they've asked to join say "Request sent" (GET /me/join-requests,
 * the same query key as My Clubs and the hub, so all three stay in step).
 *
 * Used on My Clubs' no-club landing and on /find-club.
 */

// The server's rule (lib/clubSearch.js): at least 3 letters in one word.
// Checked here too so a half-typed word never costs a request.
const MIN_LETTERS = 3;
const longEnough = (s) => s.split(/\s+/).some(w => (w.match(/[\p{L}\p{N}]/gu) || []).length >= MIN_LETTERS);
const DEBOUNCE_MS = 350;

// Site-relative logos (/share/logo/…) need the API's origin in the app.
const logoSrc = apiAssetSrc;

const ResultLogo = ({ club }) => {
  const [broken, setBroken] = useState(false);
  if (club.logo_url && !broken) {
    return (
      <span className="find-club-logo find-club-logo-img">
        <img src={logoSrc(club.logo_url)} alt="" loading="lazy" onError={() => setBroken(true)} />
      </span>
    );
  }
  return <span className="find-club-logo" aria-hidden="true">{club.name?.charAt(0)?.toUpperCase() || '?'}</span>;
};

const FindYourClub = ({ autoFocus = false }) => {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setTerm(text.replace(/\s+/g, ' ').trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [text]);

  const ready = !!user && longEnough(term);
  const { data, isFetching, error } = useQuery({
    queryKey: ['club-search', term.toLowerCase()],
    queryFn: () => axios.get('/club-search', { params: { q: term } }).then(r => r.data),
    enabled: ready,
    staleTime: 60000,
    retry: false, // a 429 must not be retried into a longer wait
    placeholderData: (prev) => prev, // keep the last results on screen while the next word loads
  });

  // "Request sent" / "Your club" on the results. Staff hold no club rows of their own.
  const { data: waiting = [] } = useQuery({
    queryKey: ['join-requests'],
    queryFn: () => axios.get('/me/join-requests').then(r => (Array.isArray(r.data) ? r.data : [])),
    enabled: !!user && !user.isPlatformUser,
    staleTime: 30000,
  });
  const mine = new Set((user?.memberships || []).filter(m => !m.implicit).map(m => m.clubSlug));
  const asked = new Set(waiting.map(r => r.club_slug));

  const typed = text.trim();
  const clubs = ready ? (data?.clubs || []) : [];
  let status = null;
  if (typed && !longEnough(typed)) status = `Keep typing — at least ${MIN_LETTERS} letters.`;
  else if (ready && error) status = error.response?.data?.error || "We couldn't search just now. Please try again.";
  else if (ready && isFetching && !data) status = 'Searching…';
  else if (ready && data && clubs.length === 0 && !isFetching) {
    status = 'No club on Marakin matches that. Check the spelling or try the town. Not every club is on Marakin yet, and new clubs appear once Marakin has checked them. Someone at the club can send you an invite link.';
  }

  return (
    <div className="find-club" data-testid="find-your-club">
      <div className="find-club-search">
        <Search size={18} className="find-club-search-icon" aria-hidden="true" />
        <input
          type="search"
          className="input find-club-input"
          placeholder="Club name or town"
          aria-label="Search for your club by name or town"
          value={text}
          onChange={e => setText(e.target.value.slice(0, 60))}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          autoFocus={autoFocus}
        />
      </div>

      <p className="find-club-status" aria-live="polite">{status || ''}</p>

      {clubs.length > 0 && (
        <ul className="find-club-results" aria-label="Clubs found">
          {clubs.map(c => {
            const meta = [c.sport, c.town].filter(Boolean).join(' · ');
            return (
              <li key={c.slug}>
                <Link to={`/${c.slug}`} className="find-club-result">
                  <ResultLogo club={c} />
                  <span className="find-club-result-info">
                    <span className="find-club-result-name">{c.name}</span>
                    {meta && <span className="find-club-result-meta">{meta}</span>}
                  </span>
                  {mine.has(c.slug) ? (
                    <span className="find-club-chip find-club-chip-mine"><Check size={12} /> Your club</span>
                  ) : asked.has(c.slug) ? (
                    <span className="find-club-chip find-club-chip-asked"><Hourglass size={12} /> Request sent</span>
                  ) : (
                    <span className="find-club-chip find-club-chip-go">View</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      {clubs.length > 0 && data?.capped && (
        <p className="find-club-more">Showing the first {data.limit || 10}. Add the town to narrow it down.</p>
      )}
    </div>
  );
};

export default FindYourClub;
